// Source slots: loading, replacing (F-01.7), frame mapping detection (F-01.8), alignment defaults (D-38).

import { SourceClient, isSequence, mediaName, type MediaInput } from '../media/source-client';
import { sequenceIdentity } from '../media/sequence-input';
import type { OpenedInfo } from '../media/protocol';
import type { Project, Source, SourceFileInfo, SourceId, VideoSpec } from '../core/types';
import { B_IDS, SOURCE_IDS } from '../core/types';
import { buildTimeline, holdFrame, rescaleTickRange, sourceFrameAt, type SourceFrameAt, type Timeline, type TimelineSource } from '../core/timeline';
import { detectMapping } from '../core/spec-check';
import { compareScaleOf, presetTransform, type AlignmentSuggestion, type GrayImage } from '../core/alignment';
import { fingerprintFile } from '../core/project';
import { remoteFromName } from '../core/remote-media';
import { resolveColor, type ColorSetup } from '../core/color';
import type { Store } from './store';
import type { DecodedFrame } from '../media/protocol';

export interface SlotRuntime {
  client: SourceClient;
  info: OpenedInfo;
  /** File handle of a video, or the folder of an image sequence (re-open without asking, D-28). */
  handle?: FileSystemHandle;
}

/** File identity stored in the project: the file itself, or the whole sequence (F-01.12). */
export async function mediaIdentity(media: MediaInput): Promise<SourceFileInfo> {
  if (isSequence(media)) return sequenceIdentity(media);
  return { name: media.name, size: media.size, lastModified: media.lastModified, fingerprint: await fingerprintFile(media) };
}

/** Shot name suggested by a source: file name without extension; a sequence pattern without its frame digits. */
export function baseMediaName(name: string): string {
  return name.replace(/[._\s-]*#+(\.[^.]+)$/, '').replace(/\.[^.]+$/, '');
}

export interface LoadError {
  errorKey: string;
  detail: string;
  fileName: string;
  spec?: Partial<VideoSpec>;
}

export class Sources {
  readonly runtime = new Map<SourceId, SlotRuntime>();
  readonly loading = new Set<SourceId>();
  readonly errors = new Map<SourceId, LoadError>();
  readonly suggestions = new Map<SourceId, AlignmentSuggestion>();
  readonly estimating = new Set<SourceId>();
  timeline: Timeline = buildTimeline([{ id: 'A', frameCount: 1, frameRate: 24, frameMapping: { mode: 'one-to-one' }, frameOffset: 0 }]);
  /** Called when a frame or proxy arrives (the view re-renders). */
  onFrame: (() => void) | null = null;
  onChange: (() => void) | null = null;
  budgetTotal = 3 * 1024 * 1024 * 1024;

  constructor(private store: Store) {}

  get project(): Project {
    return this.store.state.project;
  }

  source(id: SourceId): Source | undefined {
    return this.project.sources.find((s) => s.id === id);
  }

  has(id: SourceId): boolean {
    return this.runtime.has(id) && !!this.source(id);
  }

  loadedIds(): SourceId[] {
    return SOURCE_IDS.filter((id) => this.has(id));
  }

  loadedBs(): SourceId[] {
    return B_IDS.filter((id) => this.has(id));
  }

  client(id: SourceId): SourceClient | undefined {
    return this.runtime.get(id)?.client;
  }

  color(id: SourceId): ColorSetup {
    const s = this.source(id)!;
    return resolveColor(s.spec, s.colorOverride);
  }

  /** Loads (or replaces) a slot. Keeps per-source settings when replacing (F-01.7). */
  private loadSeq = new Map<SourceId, number>();
  private loadCounter = 0;

  /** Changes when the shown shot changes (F-18): loads started earlier are discarded. */
  private epoch = 0;

  async load(id: SourceId, media: MediaInput, handle?: FileSystemHandle, epoch = this.epoch): Promise<{ ok: boolean; frameCountChanged?: boolean }> {
    if (epoch !== this.epoch) return { ok: false };
    // Overlapping loads into the same slot: only the latest one wins.
    const seq = ++this.loadCounter;
    this.loadSeq.set(id, seq);
    const superseded = () => this.loadSeq.get(id) !== seq || epoch !== this.epoch;
    this.loading.add(id);
    this.errors.delete(id);
    this.suggestions.delete(id);
    this.onChange?.();
    const client = new SourceClient(media);
    const fileName = mediaName(media);
    let info: OpenedInfo;
    try {
      info = await client.open();
    } catch (e) {
      client.dispose();
      if (superseded()) return { ok: false };
      const err = e as { errorKey?: string; detail?: string; spec?: Partial<VideoSpec> };
      this.errors.set(id, { errorKey: err.errorKey ?? 'error.open.unreadable', detail: err.detail ?? String(e), fileName, spec: err.spec });
      this.loading.delete(id);
      this.onChange?.();
      return { ok: false };
    }
    if (info.spec.decodable === false) {
      client.dispose();
      if (superseded()) return { ok: false };
      this.errors.set(id, { errorKey: 'error.open.undecodable', detail: info.spec.codec, fileName, spec: info.spec });
      this.loading.delete(id);
      this.onChange?.();
      return { ok: false };
    }
    const identity = await mediaIdentity(media);
    if (superseded()) {
      client.dispose();
      return { ok: false };
    }
    const old = this.runtime.get(id);
    if (old) old.client.dispose();
    this.runtime.set(id, { client, info, handle });
    client.events.onFrame = () => this.onFrame?.();
    client.events.onProxy = () => this.onFrame?.();
    client.events.onScanProgress = () => this.onChange?.();
    client.events.onSpecChanged = (prevSpec) => {
      this.recomputeMappingsAndAlignment({ changed: id, prevSpec });
      this.onChange?.();
    };
    const prev = this.source(id);
    const frameCountChanged = !!prev && prev.spec.frameCount !== info.spec.frameCount;
    this.store.project(
      (p) => {
        const entry: Source = {
          id,
          role: id === 'A' ? 'original' : 'ai',
          displayName: prev?.displayName ?? '',
          file: identity,
          sequence: isSequence(media)
            ? { kind: media.kind, pattern: media.pattern, first: media.first, count: media.files.length, exrView: media.exrView }
            : undefined,
          remote: isSequence(media) ? undefined : (remoteFromName(fileName) ?? undefined),
          spec: info.spec,
          colorOverride: prev?.colorOverride,
          frameMapping: prev?.frameMappingManual ? prev.frameMapping : { mode: 'one-to-one' },
          frameMappingManual: prev?.frameMappingManual,
          frameOffset: prev?.frameOffset ?? 0,
          spatialTransform: prev?.spatialTransform ?? { mode: 'center', dx: 0, dy: 0, sx: 1, sy: 1, appliedFromSuggestion: false },
        };
        const i = p.sources.findIndex((s) => s.id === id);
        if (i >= 0) p.sources[i] = entry;
        else {
          // Keep A, B1, B2, B3 order whatever order the loads finish in (the saved file and "unsaved" check stay stable).
          p.sources.push(entry);
          p.sources.sort((x, y) => SOURCE_IDS.indexOf(x.id) - SOURCE_IDS.indexOf(y.id));
        }
        // An unnamed shot is named after its original (A); a name taken from a B that finished loading first
        // gives way to A's.
        const fromB = p.sources.some((s) => s.id !== 'A' && s.id !== id && baseMediaName(s.file.name) === p.name);
        if (!p.name || (id === 'A' && fromB)) p.name = baseMediaName(fileName);
      },
      { undo: false },
    );
    this.loading.delete(id);
    this.recomputeMappingsAndAlignment({ changed: id, prevSpec: prev?.spec, firstLoad: !prev });
    this.rebalanceBudget();
    client.startScan(480);
    this.onChange?.();
    return { ok: true, frameCountChanged };
  }

  /** Abandons loads in progress (switching shots, F-18): their results are discarded when they finish. */
  cancelLoads(): void {
    this.epoch++;
    for (const id of SOURCE_IDS) this.loadSeq.set(id, -1);
    this.loading.clear();
  }

  /** Re-attaches a file to a slot that already exists in the project (project reopen), checking the fingerprint. */
  async relink(id: SourceId, media: MediaInput, handle?: FileSystemHandle): Promise<{ ok: boolean; fingerprintMatch: boolean }> {
    const epoch = this.epoch;
    const expected = this.source(id)?.file.fingerprint;
    const fp = (await mediaIdentity(media)).fingerprint;
    const r = await this.load(id, media, handle, epoch);
    return { ok: r.ok, fingerprintMatch: !expected || expected === fp };
  }

  remove(id: SourceId): void {
    const rt = this.runtime.get(id);
    rt?.client.dispose();
    this.runtime.delete(id);
    this.suggestions.delete(id);
    this.errors.delete(id);
    this.store.project((p) => {
      p.sources = p.sources.filter((s) => s.id !== id);
    });
    this.recomputeMappingsAndAlignment();
    this.rebalanceBudget();
    this.onChange?.();
  }

  /** Swaps two slots (F-01.10 "can be swapped in the UI"). */
  swap(a: SourceId, b: SourceId): void {
    const ra = this.runtime.get(a);
    const rb = this.runtime.get(b);
    this.runtime.delete(a);
    this.runtime.delete(b);
    if (ra) this.runtime.set(b, ra);
    if (rb) this.runtime.set(a, rb);
    this.store.project((p) => {
      for (const s of p.sources) {
        if (s.id === a) s.id = b;
        else if (s.id === b) s.id = a;
        s.role = s.id === 'A' ? 'original' : 'ai';
      }
    });
    this.suggestions.clear();
    this.recomputeMappingsAndAlignment({ all: true });
    this.onChange?.();
  }

  private rebalanceBudget(): void {
    const n = Math.max(1, this.runtime.size);
    for (const rt of this.runtime.values()) rt.client.budgetBytes = this.budgetTotal / n;
  }

  /**
   * Mapping detection for B's (unless chosen manually). Alignment presets are recomputed only when a size really
   * changed (A resized, or this B newly loaded / replaced with a different size), so applied suggestions and manual
   * alignments survive reloading and project re-linking.
   */
  recomputeMappingsAndAlignment(o: { changed?: SourceId; prevSpec?: VideoSpec; firstLoad?: boolean; all?: boolean } = {}): void {
    const a = this.source('A');
    const sizeDiffers = (x: VideoSpec | undefined, y: VideoSpec | undefined) => !x || !y || x.width !== y.width || x.height !== y.height;
    this.store.project(
      (p) => {
        if (a) {
          for (const s of p.sources) {
            if (s.id === 'A') {
              s.frameMapping = { mode: 'one-to-one' };
              s.spatialTransform = { mode: 'center', dx: 0, dy: 0, sx: 1, sy: 1, appliedFromSuggestion: false };
              continue;
            }
            if (!s.frameMappingManual) s.frameMapping = detectMapping(a.spec, s.spec).mapping;
            const A = { w: a.spec.width, h: a.spec.height };
            const B = { w: s.spec.width, h: s.spec.height };
            const t = s.spatialTransform;
            const aChanged = !!o.all || (o.changed === 'A' && sizeDiffers(o.prevSpec, a.spec));
            const bChanged = o.changed === s.id && (!!o.firstLoad || sizeDiffers(o.prevSpec, s.spec));
            if (aChanged || bChanged) {
              s.spatialTransform = presetTransform(t.mode === 'manual' ? 'center' : t.mode, A, B);
              this.suggestions.delete(s.id);
            }
          }
          p.compareScale = compareScaleOf(
            { w: a.spec.width, h: a.spec.height },
            p.sources.filter((s) => s.id !== 'A').map((s) => ({ w: s.spec.width, h: s.spec.height })),
          );
          const cx = p.layout.crosshair.position;
          if (cx.x >= a.spec.width || cx.y >= a.spec.height || (cx.x === 960 && cx.y === 540 && a.spec.width !== 1920)) {
            p.layout.crosshair.position = { x: a.spec.width / 2, y: a.spec.height / 2 };
          }
        }
      },
      { undo: false },
    );
    this.rebuildTimeline();
  }

  rebuildTimeline(): void {
    const list: TimelineSource[] = [];
    const a = this.source('A');
    const aRt = this.runtime.get('A');
    if (!a || !aRt) {
      // Without A the timeline follows the longest loaded source (UI still usable while A is missing).
      const ref = this.loadedIds()[0];
      const s = ref ? this.source(ref)! : undefined;
      list.push({ id: 'A', frameCount: s?.spec.frameCount ?? 1, frameRate: s?.spec.frameRate ?? 24, frameMapping: { mode: 'one-to-one' }, frameOffset: 0 });
    } else list.push(this.tlSource('A')!);
    for (const id of this.loadedBs()) list.push(this.tlSource(id)!);
    this.timeline = buildTimeline(list);
    this.rescaleStoredTicks();
  }

  /**
   * Stored ticks (playhead, in/out) are kept in project.tickScale units. When the tick rate
   * changes, convert them — but only when every source of the project is loaded, so a project reopened before its
   * interpolated B is re-linked keeps its original values (D-19).
   */
  private rescaleStoredTicks(): void {
    const p = this.project;
    const to = this.timeline.ticksPerFrame;
    const from = p.tickScale || 1;
    if (from === to) return;
    if (p.sources.some((s) => !this.runtime.has(s.id))) return;
    const pt = rescaleTickRange(p.playback.tick, p.playback.tick, from, to).start;
    p.playback.tick = pt;
    if (p.playback.inPoint !== undefined) p.playback.inPoint = rescaleTickRange(p.playback.inPoint, p.playback.inPoint, from, to).start;
    if (p.playback.outPoint !== undefined) p.playback.outPoint = rescaleTickRange(p.playback.outPoint, p.playback.outPoint, from, to).end;
    p.tickScale = to;
  }

  tlSource(id: SourceId): TimelineSource | undefined {
    const s = this.source(id);
    const rt = this.runtime.get(id);
    if (!s || !rt) return undefined;
    return {
      id,
      frameCount: s.spec.frameCount,
      frameRate: s.spec.frameRate,
      frameMapping: s.frameMapping,
      frameOffset: id === 'A' ? 0 : s.frameOffset,
      timestamps: rt.info.timestamps,
    };
  }

  frameAt(id: SourceId, tick: number): SourceFrameAt | null {
    const src = this.tlSource(id);
    if (!src) return null;
    if (!this.has('A') && id !== 'A') {
      // No A: show each source's own frame at the A-less timeline position.
      const f = Math.floor(tick / this.timeline.ticksPerFrame);
      const raw = f + src.frameOffset;
      return holdFrame(raw, src.frameCount, { rawFrame: raw, sub: 0, noCounterpart: false, approximate: false });
    }
    return sourceFrameAt(this.timeline, src, tick);
  }

  /** Luma image downscaled to ~480 px wide from a decoded frame's Y plane (or RGBA), for alignment estimation. */
  static grayFromFrame(f: DecodedFrame, targetW = 480): GrayImage {
    const scale = targetW / f.width;
    const w = Math.max(1, Math.round(f.width * scale));
    const h = Math.max(1, Math.round(f.height * scale));
    const out = new Float32Array(w * h);
    const l0 = f.layout[0];
    const high = f.format.includes('P1');
    const maxIn = high ? (f.format.includes('P12') ? 4095 : 1023) : 255;
    const u8 = new Uint8Array(f.data);
    const u16 = high ? new Uint16Array(f.data) : null;
    const w16 = f.format === 'RGBA16' ? new Uint16Array(f.data) : null;
    const step = f.width / w;
    for (let y = 0; y < h; y++) {
      const sy0 = Math.floor(y * step);
      const sy1 = Math.min(f.height, Math.floor((y + 1) * step));
      for (let x = 0; x < w; x++) {
        const sx0 = Math.floor(x * step);
        const sx1 = Math.min(f.width, Math.floor((x + 1) * step));
        let sum = 0;
        let n = 0;
        for (let yy = sy0; yy < sy1; yy += 1) {
          for (let xx = sx0; xx < sx1; xx += 1) {
            let v: number;
            if (f.format === 'RGBA16') {
              const o = (l0.offset + yy * l0.stride) / 2 + xx * 4;
              v = (0.2126 * w16![o] + 0.7152 * w16![o + 1] + 0.0722 * w16![o + 2]) / 257;
            } else if (f.format === 'RGBA') {
              const o = l0.offset + yy * l0.stride + xx * 4;
              v = 0.2126 * u8[o] + 0.7152 * u8[o + 1] + 0.0722 * u8[o + 2];
            } else if (u16) v = (u16[(l0.offset + yy * l0.stride) / 2 + xx] * 255) / maxIn;
            else v = u8[l0.offset + yy * l0.stride + xx];
            sum += v;
            n++;
          }
        }
        out[y * w + x] = n ? sum / n : 0;
      }
    }
    return { w, h, data: out };
  }

  /**
   * Suggests an alignment preset for a B at the current tick (F-03.8). Uses the central region, or the person mask
   * (weight) when provided (Phase 3).
   */
  async estimate(id: SourceId, tick: number, weight?: GrayImage, kind: 'preset' | 'free' = 'preset'): Promise<AlignmentSuggestion | null> {
    const a = this.source('A');
    const b = this.source(id);
    const ca = this.client('A');
    const cb = this.client(id);
    if (!a || !b || !ca || !cb) return null;
    const at = this.frameAt('A', tick);
    const bt = this.frameAt(id, tick);
    // Only frames that really correspond: a held end frame would estimate against the wrong picture (D-26).
    if (!at || !bt || at.held || bt.held) return null;
    const fa = at.frame;
    const fb = bt.frame;
    if (fa === null || fb === null) return null;
    this.estimating.add(id);
    this.onChange?.();
    try {
      const [da, db] = await Promise.all([waitFor(ca, fa), waitFor(cb, fb)]);
      if (!da || !db) return null;
      const ga = Sources.grayFromFrame(da);
      const gb = Sources.grayFromFrame(db, Math.round((480 * db.width) / da.width));
      const s = await runAlignWorker(kind, ga, gb, { w: a.spec.width, h: a.spec.height }, { w: b.spec.width, h: b.spec.height }, weight);
      if (this.client(id) !== cb || this.client('A') !== ca) return null;
      if (s) this.suggestions.set(id, s);
      return s;
    } finally {
      this.estimating.delete(id);
      this.onChange?.();
    }
  }
}

let alignWorker: Worker | null = null;
let alignSeq = 0;

/** Runs a preset suggestion (F-03.8) or a free shift/scale estimate (F-03.2) in a worker. */
export function runAlignWorker(
  kind: 'preset' | 'free',
  a: GrayImage,
  b: GrayImage,
  aFull: { w: number; h: number },
  bFull: { w: number; h: number },
  weight?: GrayImage,
): Promise<AlignmentSuggestion | null> {
  alignWorker ??= new Worker(new URL('../workers/align-worker.ts', import.meta.url), { type: 'module' });
  const w = alignWorker;
  const id = ++alignSeq;
  return new Promise((resolve) => {
    const onMsg = (ev: MessageEvent<{ id: number; ok: boolean; suggestion?: AlignmentSuggestion }>) => {
      if (ev.data.id !== id) return;
      w.removeEventListener('message', onMsg);
      resolve(ev.data.ok ? (ev.data.suggestion ?? null) : null);
    };
    w.addEventListener('message', onMsg);
    w.postMessage({ id, kind, a, b, aFull, bFull, weight });
  });
}

/** Waits for a frame to be decoded (requests it if needed). */
export function waitFor(c: SourceClient, index: number, timeoutMs = 15000): Promise<DecodedFrame | null> {
  const f = c.getFrame(index);
  if (f) return Promise.resolve(f);
  return new Promise((resolve) => {
    const t = setTimeout(() => {
      off();
      resolve(null);
    }, timeoutMs);
    const off = c.addFrameListener((i) => {
      if (i === index) {
        clearTimeout(t);
        off();
        resolve(c.getFrame(index) ?? null);
      }
    });
    c.need(index);
  });
}
